import * as BiIcons from "react-icons/bi";

import React, {
    forwardRef,
    type HTMLAttributes,
    type KeyboardEvent,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import "../../../static/css/terminal-search.css";
import TerminalContainerContext from "./TerminalContainerContext.ts";
import {useTranslation} from "react-i18next";

// type TerminalSearchDialogProps = HTMLAttributes<HTMLDialogElement>;
interface TerminalSearchDialogProps extends HTMLAttributes<HTMLDialogElement> {
    t?: string;
}

export interface TerminalSearchDialogRef {
    show(left: number, top: number): void;

    close(): void;
}


const TerminalSearchDialog = forwardRef<TerminalSearchDialogRef, TerminalSearchDialogProps>(
    (props, ref) => {
        const {t} = useTranslation("ui");
        const context = useContext(TerminalContainerContext)!;
        const dialogRef = useRef<HTMLDialogElement>(null);
        const headerRef = useRef<HTMLDivElement>(null);
        const [isDragging, setIsDragging] = useState(false);
        const dragOffset = useRef({x: 0, y: 0});
        const [text, setText] = useState('');
        const [isCaseSensitive, setCaseSensitive] = useState(false);
        const [isWholeWord, setWholeWord] = useState(false);
        const [isRegEx, setRegEx] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);

        const openDialog = () => {
            if (dialogRef.current) {
                dialogRef.current.show();
            }
        };

        const closeDialog = () => {
            if (dialogRef.current) {
                dialogRef.current.close();
            }
        };

        const handleMouseDown = (e: React.MouseEvent) => {
            setIsDragging(true);
            const rect = dialogRef.current!.getBoundingClientRect();
            dragOffset.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        };

        const handleMouseMove = useCallback(
            (e: MouseEvent) => {
                if (isDragging && dialogRef.current) {
                    const newX = e.clientX - dragOffset.current.x;
                    const newY = e.clientY - dragOffset.current.y;
                    dialogRef.current.style.left = newX + "px";
                    dialogRef.current.style.top = newY + "px";
                }
            },
            [isDragging]
        );

        const handleMouseUp = useCallback(() => {
            setIsDragging(false);
        }, [setIsDragging]);

        useImperativeHandle(ref, () => ({
            close() {
                closeDialog()
            },
            show(left: number, top: number) {
                const dialog = dialogRef.current!;
                if (dialog) {
                    dialog.style.left = left + "px";
                    dialog.style.top = top + "px";
                }
                openDialog();
                inputRef.current?.focus();
            }
        }));

        useEffect(() => {
            if (isDragging) {
                window.addEventListener("mousemove", handleMouseMove);
                window.addEventListener("mouseup", handleMouseUp);
            } else {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
            }

            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
            };
        }, [isDragging, handleMouseMove, handleMouseUp]);

        const onClickPrev = useCallback(() => {
            console.log(context.findPrevious(text, {
                caseSensitive: isCaseSensitive,
                wholeWord: isWholeWord,
                regex: isRegEx,
            }));
        }, [context, text, isCaseSensitive, isWholeWord, isRegEx]);

        const onClickNext = useCallback(() => {
            context.findNext(text, {
                caseSensitive: isCaseSensitive,
                wholeWord: isWholeWord,
                regex: isRegEx,
            });
        }, [context, text, isCaseSensitive, isWholeWord, isRegEx]);

        const onClickClear = useCallback(() => {
            context.clearSearchResult();
        }, [context]);

        const onEnterInput = useCallback(
            (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && e.shiftKey) {
                    e.preventDefault();
                    onClickPrev();

                } else if (e.key === "Enter") {
                    e.preventDefault();
                    onClickNext();
                }
            },
            [onClickNext, onClickPrev]
        );

        return (
            <>
                <dialog
                    className="gnome-dialog"
                    ref={dialogRef}
                    style={{
                        position: "absolute",
                        left: props.style?.left || "0px",
                        top: props.style?.top || "0px",
                        ...props.style,
                    }}
                >
                    <section
                        className="gnome-dialog-header"
                        ref={headerRef}
                        onMouseDown={handleMouseDown}
                        style={{cursor: "move"}}
                    >
                        <h2 className="gnome-dialog-title">{t("terminal.find")}</h2>
                        <button className="gnome-dialog-close" onClick={closeDialog}>
                            ×
                        </button>
                    </section>
                    <section className="gnome-dialog-content">
                        <div className="gnome-search-bar">
                            <input
                                ref={inputRef}
                                type="text"
                                className="gnome-search-input"
                                placeholder="Search"
                                defaultValue={text}
                                onChange={e => setText(e.target.value)}
                                onKeyDown={onEnterInput}
                            />

                            <div className="gnome-search-action" onMouseDown={(e) => e.preventDefault()}>
                                <button onClick={onClickClear}>
                                    <BiIcons.BiEraser/>
                                </button>
                                <button className="gnome-search-up" onClick={onClickPrev}>
                                    <BiIcons.BiCaretUp/>
                                </button>
                                <button className="gnome-search-down" onClick={onClickNext}>
                                    <BiIcons.BiCaretDown/>
                                </button>
                            </div>
                        </div>
                        <div className="gnome-options">
                            <label>
                                <input type="checkbox"
                                       onChange={() => setCaseSensitive(!isCaseSensitive)}
                                       defaultChecked={isCaseSensitive}
                                />
                                {t("terminal.matchCase")}
                            </label>
                            <label>
                                <input type="checkbox"
                                       onChange={() => setWholeWord(!isWholeWord)}
                                       defaultChecked={isWholeWord}
                                />
                                {t("terminal.onlyWordsThatAreEntirelyCorrect")}
                            </label>
                            <label>
                                <input type="checkbox"
                                       onChange={() => setRegEx(!isRegEx)}
                                       defaultChecked={isRegEx}/>
                                {t("terminal.regularExpression")}
                            </label>
                        </div>
                    </section>
                </dialog>
            </>
        );
    });

export default TerminalSearchDialog;
